import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useBillingStore, BillingTransaction } from '../../store/billingStore';
import {
  X, Loader2, Receipt, ExternalLink, CreditCard, Wallet,
  CheckCircle2, XCircle, Clock, RefreshCw, AlertTriangle,
} from 'lucide-react';

interface Props {
  txId: string;
  onClose: () => void;
}

const typeLabel: Record<string, string> = {
  game_purchase: 'Achat de jeu',
  wallet_topup: 'Recharge portefeuille',
  hosting_subscription: 'Hébergement',
  erozion_plus: 'Erozion Plus',
  refund: 'Remboursement',
};

const statusConfig: Record<string, { label: string; cls: string; Icon: React.ElementType }> = {
  completed: { label: 'Complété', cls: 'text-success bg-success/10 border-success/30', Icon: CheckCircle2 },
  pending: { label: 'En attente', cls: 'text-warning bg-warning/10 border-warning/30', Icon: Clock },
  failed: { label: 'Échoué', cls: 'text-error bg-error/10 border-error/30', Icon: XCircle },
  refunded: { label: 'Remboursé', cls: 'text-blue-400 bg-blue-400/10 border-blue-400/30', Icon: RefreshCw },
  partially_refunded: { label: 'Partiellement remboursé', cls: 'text-blue-400 bg-blue-400/10 border-blue-400/30', Icon: RefreshCw },
  disputed: { label: 'En litige', cls: 'text-orange-400 bg-orange-400/10 border-orange-400/30', Icon: AlertTriangle },
};

const TransactionDetailModal: React.FC<Props> = ({ txId, onClose }) => {
  const { fetchTransactionDetail } = useBillingStore();
  const [tx, setTx] = useState<BillingTransaction | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const detail = await fetchTransactionDetail(txId);
      setTx(detail);
      setLoading(false);
    })();
  }, [txId]);

  const status = tx ? (statusConfig[tx.status] || statusConfig.pending) : statusConfig.pending;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-bg-primary rounded-2xl w-full max-w-md border border-border-color shadow-2xl shadow-black/50 animate-slide-up overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-color">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-accent-color" />
            <h2 className="text-base font-bold text-text-primary">Détails de la transaction</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
          </div>
        ) : !tx ? (
          <div className="px-6 py-12 text-center text-text-muted text-sm">
            Transaction introuvable.
          </div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Status + Amount hero */}
            <div className="text-center">
              <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${status.cls}`}>
                <status.Icon className="h-3.5 w-3.5" />
                {status.label}
              </div>
              <p className="text-3xl font-bold text-text-primary mt-3 tabular-nums">
                {tx.type === 'refund' ? '+' : ''}{Number(tx.amount).toFixed(2)}
                <span className="text-base font-normal text-text-muted ml-1">{tx.currency.toUpperCase()}</span>
              </p>
            </div>

            {/* Details grid */}
            <div className="space-y-3">
              <DetailRow label="Type" value={typeLabel[tx.type] || tx.type} />
              {tx.item_name && <DetailRow label="Article" value={tx.item_name} />}
              {tx.description && <DetailRow label="Description" value={tx.description} />}
              <DetailRow
                label="Date"
                value={new Date(tx.created_at).toLocaleDateString('fr-CA', {
                  year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit',
                })}
              />

              {/* Payment breakdown */}
              {(Number(tx.wallet_amount) > 0 || Number(tx.stripe_amount) > 0) && (
                <div className="pt-2 border-t border-border-color/50 space-y-2">
                  {Number(tx.wallet_amount) > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-text-muted">
                        <Wallet className="h-3.5 w-3.5" /> Portefeuille
                      </span>
                      <span className="text-text-primary font-medium tabular-nums">
                        {Number(tx.wallet_amount).toFixed(2)} {tx.currency.toUpperCase()}
                      </span>
                    </div>
                  )}
                  {Number(tx.stripe_amount) > 0 && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2 text-text-muted">
                        <CreditCard className="h-3.5 w-3.5" /> Carte
                      </span>
                      <span className="text-text-primary font-medium tabular-nums">
                        {Number(tx.stripe_amount).toFixed(2)} {tx.currency.toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {tx.stripe_payment_intent_id && (
                <DetailRow label="Référence Stripe" value={tx.stripe_payment_intent_id} mono />
              )}
            </div>

            {/* Receipt button */}
            {tx.receipt_url && (
              <a
                href={tx.receipt_url}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-accent-color/10 text-accent-color text-sm font-semibold hover:bg-accent-color/20 transition-colors border border-accent-color/20"
              >
                <ExternalLink className="h-4 w-4" />
                Voir la facture Stripe
              </a>
            )}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

/* Small helper */
const DetailRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4">
    <span className="text-xs text-text-muted shrink-0">{label}</span>
    <span className={`text-sm text-text-primary text-right ${mono ? 'font-mono text-xs break-all' : ''}`}>
      {value}
    </span>
  </div>
);

export default TransactionDetailModal;
